import { useEffect, useState, useRef } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useMutation } from "@tanstack/react-query"
import { useOnAuthSuccess } from "@/hooks/useAuth"
import { githubCallback } from "@/lib/http/auth"
import { ROUTES } from "@/routes/path"
import { useToast } from "@/hooks/useToast"
import { LoadingOverlay } from "@/components/ui/loading-overlay"

export default function GitHubCallback() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { toast } = useToast()
  const [error, setError] = useState<string | null>(null)
  const hasProcessedRef = useRef(false)
  const onAuthSuccess = useOnAuthSuccess()

  const mutation = useMutation({
    mutationFn: async () => {
      const code = searchParams.get("code")
      const error = searchParams.get("error")

      if (error) {
        throw new Error(error)
      }

      if (!code) {
        throw new Error("No authorization code received")
      }

      return await githubCallback(code)
    },
    onSuccess: (data) => {
      if (data?.token) {
        onAuthSuccess(data)
      } else {
        setError("Invalid response from authentication server")
      }
    },
    onError: (error: any) => {
      console.error("GitHub callback error:", error)
      setError(error.message || "Failed to authenticate with GitHub")
      toast({
        title: "Authentication Error",
        description: error.message || "Failed to authenticate with GitHub. Please try again.",
        variant: "destructive",
      })
    },
  })

  useEffect(() => {
    if (!hasProcessedRef.current) {
      hasProcessedRef.current = true
      mutation.mutate()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  if (error) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50 dark:bg-zinc-900">
        <div className="text-center">
          <h1 className="mb-4 text-2xl font-semibold text-gray-900 dark:text-zinc-100">Authentication Failed</h1>
          <p className="mb-6 text-gray-600 dark:text-zinc-400">{error}</p>
          <button
            onClick={() => navigate(ROUTES.AUTH.LOGIN)}
            className="rounded-lg bg-primary px-4 py-2 text-white hover:bg-primary/90"
          >
            Back to Login
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-screen items-center justify-center bg-gray-50 dark:bg-zinc-900">
      <LoadingOverlay text="Completing authentication..." />
    </div>
  )
}
